import React, { useState, useMemo } from 'react';
import { LoadingIndicator, SearchInput, Select } from '@ygtechnologies/bygr-frontend-component-library';
import {
    getImportableCatalogProducts,
    importProductsToProject,
    ProjectImportableProduct,
} from '@/services/profile-catalog-products';
import { Trans } from '@lingui/react/macro';
import { useAppSelector, useMergeState } from '@/utils/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAvailableCatalogs, ProfileCatalogDto } from '@/services/profile-catalog';
import { Button, ButtonBar, Checkbox, Dialog, Section } from '@ygtechnologies/rubus-ui';
import { ProjectCatalogImportItem } from './ProjectCatalogImportItem';
import { ProductCardList } from '../../products/ProductCard';
import theme from './ProjectCatalogImportDialog.module.scss';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ProjectCatalogImportDialogProps {
    show: boolean;
    onClose(): void;
    onCancel?(): void;
}

interface Filters {
    showAvailableOnly: boolean;
    text: string;
}

function getDefaultFilters(): Filters {
    return {
        showAvailableOnly: false,
        text: '',
    };
}

export const ProjectCatalogImportDialog: React.FC<ProjectCatalogImportDialogProps> = ({ show, onClose }) => {
    const profile = useAppSelector((state) => state.global.activeProfile!);
    const project = useAppSelector((state) => state.global.activeProject!);

    const [filters, mergeFilters, setFilters] = useMergeState(getDefaultFilters());
    const [selectedProfileId, setSelectedProfileId] = useState(profile.id);
    const [saving, setSaving] = useState(false);

    const queryClient = useQueryClient();
    const { data: catalogs } = useQuery({
        queryKey: ['availableCatalogs', project.currencyCode],
        queryFn: () => getAvailableCatalogs(project.currencyCode),
        enabled: show,
    });

    const { data: products } = useQuery({
        queryKey: ['importableProducts', selectedProfileId, project.id],
        queryFn: () => getImportableCatalogProducts(selectedProfileId, project.id),
        enabled: show,
    });

    const importProducts = useMutation({
        mutationFn: (productIds: number[]) => importProductsToProject(project.id, productIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['importableProducts', selectedProfileId, project.id] });
        },
    });

    const reset = () => {
        setFilters(getDefaultFilters());
        setSelectedProfileId(profile.id);
    };

    const handleOpen = async () => {
        reset();
    };

    const handleImport = async (item: ProjectImportableProduct) => {
        setSaving(true);
        try {
            await importProducts.mutateAsync([item.product.id]);
        } finally {
            setSaving(false);
        }
    };

    const filteredProducts = useMemo(() => {
        if (!products) {
            return null;
        }

        var data = [...products.filter((x) => x.isImportable), ...products.filter((x) => !x.isImportable)];

        return data.filter((p) => {
            if (filters.showAvailableOnly && !p.isImportable) {
                return false;
            }

            if (filters.text && !p.product.name.toLowerCase().includes(filters.text.toLowerCase())) {
                return false;
            }

            return true;
        });
    }, [products, filters]);

    return (
        <Dialog open={show} onClose={onClose} onOpen={handleOpen} alignment="top" heightLimit="window">
            <Dialog.Content contentWidth="lg" className={theme.content}>
                <Dialog.Header className={theme.header}>
                    <Trans>Import products to {project.name}</Trans>
                    <FontAwesomeIcon
                        icon={faXmark}
                        onClick={onClose}
                        className={theme.closeButton}
                    />
                </Dialog.Header>
                <Section variant="highlight">
                    <Trans>Selected catalog</Trans>
                    <Select<ProfileCatalogDto>
                        onChange={(v: any) => setSelectedProfileId(v.profileId)}
                        options={catalogs}
                        value={catalogs?.find((x) => x.profileId === selectedProfileId)}
                        getOptionValue={(x) => x.profileId.toString()}
                        getOptionLabel={(x) => `${x.name} (${x.productCount})`}
                    />
                </Section>
                <Section variant="highlight">
                    <SearchInput searchText={filters.text} onSearchTextChange={(e) => mergeFilters({ text: e })} />
                    <div className={theme.extraFilters}>
                        <Checkbox
                            inputSize="md"
                            checked={filters.showAvailableOnly}
                            onChange={(e) => mergeFilters({ showAvailableOnly: e.target.checked })}>
                            <Trans>Hide already imported products</Trans>
                        </Checkbox>
                    </div>
                </Section>
                <Section className={theme.productsContainer}>
                    {filteredProducts ? (
                        <ProductCardList>
                            {filteredProducts.map((item) => (
                                <ProjectCatalogImportItem key={item.product.id} item={item} onImport={handleImport} />
                            ))}
                        </ProductCardList>
                    ) : (
                        <LoadingIndicator />
                    )}
                </Section>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="primary" onClick={onClose} disabled={saving}>
                            <Trans>Close</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
